import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { CheckCircle, ArrowRight, Shield, Clock, Users, Star, DollarSign, FileText, Calendar, CreditCard, FileCheck, ShieldCheck } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ContactModal from '../../components/ContactModal';
import { openChat } from '../../utils/chat';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';

const RegisterBusinessPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const benefits = [
    {
      icon: Shield,
      title: 'Protect Your Personal Assets',
      description: 'Keep your personal assets separate from your business liabilities',
    },
    {
      icon: Clock,
      title: 'Quick & Easy Filing',
      description: 'Get started in minutes with our simple online process',
    },
    {
      icon: Users,
      title: 'Expert Support',
      description: 'Access to legal professionals whenever you need guidance',
    },
  ];

  const packages = [
    {
      name: 'Basic',
      price: '$149',
      stateFees: '+ state fees',
      features: [
        'Preparation & filing of formation documents',
        'Unlimited company name searches',
        'Online access to your documents',
        'Free registered agent service (first year)',
        'Email support',
      ],
      popular: false,
    },
    {
      name: 'Standard',
      price: '$249',
      stateFees: '+ state fees',
      features: [
        'Everything in Basic',
        'Organizational documents',
        'Banking Resolution',
        'EIN (Federal Tax ID)',
        'Express shipping',
        'Priority support',
      ],
      popular: true,
    },
    {
      name: 'Premium',
      price: '$399',
      stateFees: '+ state fees',
      features: [
        'Everything in Standard',
        'Business website domain',
        'Business license research report',
        'Rush filing with Secretary of State',
        'Expedited processing',
        'Dedicated support specialist',
      ],
      popular: false,
    },
  ];

  const steps = [
    {
      number: '01',
      title: 'Choose your state',
      description: 'Select the state where you want to register your business',
    },
    {
      number: '02',
      title: 'Choose Business Name',
      description: 'Choose a unique business name that complies with state requirements',
    },
    {
      number: '03',
      title: 'Provide company details',
      description: 'Enter your business information and member details',
    },
    {
      number: '04',
      title: 'Review and submit',
      description: 'We file your paperwork with the state and keep you updated',
    },
  ];

  const faqs = [
    {
      question: 'What business structure should I choose?',
      answer: 'The best structure depends on your needs. LLCs offer liability protection and flexibility. Corporations provide strong liability protection and easier fundraising. Sole Proprietorships are simplest but offer no liability protection. We help you choose the right option.',
    },
    {
      question: 'How long does business registration take?',
      answer: 'Processing time varies by state and business type, typically 5-15 business days. Some states offer expedited processing. We handle all paperwork and keep you updated throughout the process.',
    },
    {
      question: 'What documents do I need?',
      answer: 'Required documents vary by business structure and state. We guide you through the process and prepare all necessary formation documents, including articles of organization/incorporation and operating agreements or bylaws.',
    },
    {
      question: 'What are ongoing business requirements?',
      answer: 'Businesses typically need to file annual reports, pay annual fees, maintain a registered agent, and stay compliant with state requirements. We offer compliance services to keep your business in good standing.',
    },
    {
      question: 'Can I register in any state?',
      answer: 'Yes, you can register in any state. Most businesses form in their home state for convenience, but some choose states like Delaware or Nevada for their business-friendly laws and tax benefits.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 -left-4 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Register Your Business
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Choose the right business structure and get your business registered quickly and correctly. Professional business registration services for LLCs, Corporations, and more.
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Button
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => setIsModalOpen(true)}
                >
                  Register Your Business
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                onClick={openChat}
                >
                  Chat Now</Button>
              </div>
              <div className="mt-8 flex items-center space-x-6 text-sm text-gray-600">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  500K+ businesses registered
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  100% accuracy guarantee
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1681505504714-4ded1bc247e7"
                alt="Business Registration"
                className="rounded-lg shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Choose Your Business Structure
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Select the right business entity type based on your goals, liability concerns, and tax preferences
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => (
              <Card key={idx} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <benefit.icon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Register Your Business Properly */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Image */}
            <div>
              <img
                src="https://images.unsplash.com/photo-1763729805496-b5dbf7f00c79"
                alt="Business Registration"
                className="rounded-lg shadow-xl w-full h-[500px] object-cover"
              />
            </div>

            {/* Right side - Content */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                The foundation for business success
              </h2>
              
              <p className="text-base text-gray-700 mb-6 leading-relaxed">
                Properly registering your business is the first step to building a legitimate, protected, and credible company. The right business structure provides liability protection, tax advantages, and professional credibility that helps you grow.
              </p>

              <p className="text-base text-gray-700 mb-8 leading-relaxed">
                Choosing between an LLC, Corporation, or other structure depends on your goals. Each offers different levels of liability protection, tax treatment, and management flexibility. We help you understand the options and select what's best for your situation.
              </p>

              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Button
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => setIsModalOpen(true)}
                >
                  Register Your Business
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                  onClick={openChat}
                >
                  Chat Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tax Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Business structure tax considerations
            </h2>
            <p className="text-xl text-gray-600">
              Flexible tax options to minimize your tax burden
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Pass-Through Taxation</h3>
                <p className="text-gray-600 mb-6">
                  Different business structures offer different tax treatment. LLCs and S-Corporations benefit from pass-through taxation. C-Corporations are taxed separately but offer more flexibility for growth and fundraising.
                </p>
                <ul className="space-y-3">
                  {[
                    'Choose optimal tax structure',
                    'Maximize tax deductions',
                    'Flexible tax elections',
                    'Professional tax guidance',
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">S-Corp Election Option</h3>
                <p className="text-gray-600 mb-6">
                  Many business structures allow you to elect different tax treatments as your business grows. We help you understand the tax implications of each structure and make the right choice.
                </p>
                <ul className="space-y-3">
                  {[
                    'Liability protection options',
                    'Tax optimization strategies',
                    'Professional credibility',
                    'Scalable business structure',
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setIsModalOpen(true)}
            >
              Get Legal Help
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
              onClick={openChat}
            >
              Chat Now
            </Button>
          </div>

          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 border border-gray-700">
            <h3 className="text-2xl font-bold text-white mb-4 text-center">Common business tax deductions</h3>
            <p className="text-base text-gray-300 text-center mb-8 max-w-4xl mx-auto">
              Maximize your tax savings by taking advantage of common business deductions. Regardless of your business structure, you can reduce your taxable income by properly documenting and claiming eligible business expenses.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { title: 'Business Expenses', items: ['Office supplies', 'Equipment', 'Software', 'Marketing'] },
                { title: 'Home Office', items: ['Rent/mortgage portion', 'Utilities', 'Internet', 'Furniture'] },
                { title: 'Professional Services', items: ['Legal fees', 'Accounting', 'Consulting', 'Insurance'] },
                { title: 'Travel & Transportation', items: ['Mileage deduction', 'Business travel', 'Meals (50%)', 'Parking & tolls'] },
              ].map((category, idx) => (
                <div key={idx}>
                  <h4 className="font-semibold text-white mb-3 text-base">{category.title}</h4>
                  <div className="space-y-2">
                    {category.items.map((item, itemIdx) => (
                      <div key={itemIdx} className="text-sm text-gray-300">
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How it works
            </h2>
            <p className="text-lg text-gray-600">
              Register your business in 4 simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, idx) => (
              <div key={idx} className="relative">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white text-2xl font-bold rounded-full mb-4">
                  {step.number}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-base text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Choose your registration package
            </h2>
            <p className="text-xl text-gray-600">
              Select the package that best fits your needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, idx) => (
              <Card
                key={idx}
                className={`relative hover:shadow-xl transition-shadow flex flex-col ${
                  pkg.popular ? 'border-2 border-blue-600' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <CardContent className="p-8 flex flex-col flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{pkg.name}</h3>
                  <div className="mb-6">
                    <p className="text-lg text-gray-700 font-medium">Custom pricing based on your specific needs</p>
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {pkg.features.map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full mt-auto ${
                      pkg.popular
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50'
                    }`}
                    onClick={() => setIsModalOpen(true)}
                  >
                    Get a Quote
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What's included with every registration
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Preparation of formation documents',
              'Filing with Secretary of State',
              'Unlimited name availability searches',
              'Registered agent service (1st year)',
              'Access to online dashboard',
              'Customer support via phone & email',
              'Document delivery via email',
              'Lifetime company alerts',
              'Organizational documents',
            ].map((item, idx) => (
              <div key={idx} className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Structure Comparison */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Business Structures: Which is right for you?
            </h2>
            <p className="text-xl text-gray-600">
              Compare the key differences to make an informed decision
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-4 text-left font-bold text-gray-900">Feature</th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-blue-600">LLC</th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900">Corporation</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Formation Complexity', llc: 'Simple', corp: 'More Complex' },
                  { feature: 'Ongoing Compliance', llc: 'Minimal', corp: 'Extensive' },
                  { feature: 'Tax Flexibility', llc: 'Pass-through taxation', corp: 'Double taxation (C-Corp)' },
                  { feature: 'Ownership Structure', llc: 'Members', corp: 'Shareholders' },
                  { feature: 'Raising Capital', llc: 'Limited options', corp: 'Easy through stock sales' },
                  { feature: 'Management Structure', llc: 'Flexible', corp: 'Board of Directors required' },
                  { feature: 'Personal Liability', llc: 'Protected', corp: 'Protected' },
                  { feature: 'Best For', llc: 'Small to medium businesses', corp: 'High-growth startups' },
                ].map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 p-4 font-semibold text-gray-900">{row.feature}</td>
                    <td className="border border-gray-300 p-4 text-center text-gray-700">{row.llc}</td>
                    <td className="border border-gray-300 p-4 text-center text-gray-700">{row.corp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setIsModalOpen(true)}
            >
              Get Legal Help
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
              onClick={openChat}
            >
              Chat Now
            </Button>
          </div>
        </div>
      </section>

      {/* State Filing Times */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Business registration times by state
            </h2>
            <p className="text-lg text-gray-600">
              Typical processing times vary by state
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { state: 'California', time: '10-15 business days', expedited: 'Same day available' },
              { state: 'Delaware', time: '5-7 business days', expedited: '24 hour available' },
              { state: 'Florida', time: '7-10 business days', expedited: '48 hour available' },
              { state: 'New York', time: '2-3 weeks', expedited: '1 week available' },
              { state: 'Texas', time: '7-10 business days', expedited: '2-3 day available' },
              { state: 'Illinois', time: '10-15 business days', expedited: '24 hour available' },
              { state: 'Nevada', time: '5-7 business days', expedited: 'Same day available' },
              { state: 'Wyoming', time: '5-7 business days', expedited: '24 hour available' },
            ].map((item, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.state}</h3>
                  <p className="text-base text-gray-600 mb-2">Standard: {item.time}</p>
                  <p className="text-sm text-blue-600 font-semibold">{item.expedited}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-base text-gray-600">
              Filing times may vary. Expedited processing available in most states for an additional fee.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setIsModalOpen(true)}
            >
              Get Legal Help
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
              onClick={openChat}
            >
              Chat Now
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              What our customers say
            </h2>
            <p className="text-lg text-blue-100">Join thousands of satisfied business owners</p>
          </div>

          <div className="relative">
            <div className="flex gap-6 animate-scroll">
              {[
                {
                  name: 'Alexandra Morrison',
                  business: 'Digital Marketing Agency',
                  content: 'Legal Flick made registering my business incredibly easy. The whole process was smooth and I had my documents within a week. Highly recommend!',
                  rating: 5,
                },
                {
                  name: 'Marcus Thompson',
                  business: 'E-commerce Entrepreneur',
                  content: 'Great service and excellent customer support. They answered all my questions and guided me through every step of the process.',
                  rating: 5,
                },
                {
                  name: 'Isabella Rodriguez',
                  business: 'Business Strategy Consultant',
                  content: 'Professional, efficient, and affordable. I was able to register my business in just a few days. The organizational documents were a great bonus.',
                  rating: 5,
                },
                {
                  name: 'James Patterson',
                  business: 'Software Development Studio',
                  content: 'The registered agent service is fantastic. They handle everything professionally and keep me informed about all important documents.',
                  rating: 5,
                },
                {
                  name: 'Sophia Chen',
                  business: 'Creative Design Agency',
                  content: 'Best decision for my business! The process was straightforward, and the support team was always available to answer my questions.',
                  rating: 5,
                },
                {
                  name: 'Daniel Foster',
                  business: 'Real Estate Investment',
                  content: 'Exceptional service from start to finish. They made the entire business registration process hassle-free. Worth every penny!',
                  rating: 5,
                },
                {
                  name: 'Olivia Martinez',
                  business: 'Health & Wellness Coach',
                  content: 'I appreciated the transparency and guidance throughout. The dashboard makes it easy to track everything. Highly professional!',
                  rating: 5,
                },
                {
                  name: 'Ryan Anderson',
                  business: 'Mobile App Development',
                  content: 'Fast, reliable, and comprehensive. They handled all the paperwork efficiently and my business was registered without any issues.',
                  rating: 5,
                },
              ].concat([
                {
                  name: 'Alexandra Morrison',
                  business: 'Digital Marketing Agency',
                  content: 'Legal Flick made registering my business incredibly easy. The whole process was smooth and I had my documents within a week. Highly recommend!',
                  rating: 5,
                },
                {
                  name: 'Marcus Thompson',
                  business: 'E-commerce Entrepreneur',
                  content: 'Great service and excellent customer support. They answered all my questions and guided me through every step of the process.',
                  rating: 5,
                },
                {
                  name: 'Isabella Rodriguez',
                  business: 'Business Strategy Consultant',
                  content: 'Professional, efficient, and affordable. I was able to register my business in just a few days. The organizational documents were a great bonus.',
                  rating: 5,
                },
                {
                  name: 'James Patterson',
                  business: 'Software Development Studio',
                  content: 'The registered agent service is fantastic. They handle everything professionally and keep me informed about all important documents.',
                  rating: 5,
                },
              ]).map((testimonial, idx) => (
                <Card key={idx} className="flex-shrink-0 w-80 bg-white/10 backdrop-blur-sm border-white/20">
                  <CardContent className="p-6">
                    <div className="flex mb-3">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-base text-white mb-4 italic min-h-[80px]">&ldquo;{testimonial.content}&rdquo;</p>
                    <div>
                      <div className="font-semibold text-white">{testimonial.name}</div>
                      <div className="text-sm text-blue-100">{testimonial.business}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Related services
            </h2>
            <p className="text-lg text-gray-600">
              Additional services to help your business succeed
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Registered Agent Service',
                description: 'Professional registered agent service to handle your legal documents and ensure you never miss important notices',
              },
              {
                title: 'Business Licenses',
                description: 'Get help obtaining all required federal, state, and local licenses for your business operations',
              },
              {
                title: 'EIN (Tax ID Number)',
                description: 'Federal tax identification number (EIN) required to open business bank accounts and hire employees',
              },
            ].map((service, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-base text-gray-600 mb-6">{service.description}</p>
                  <Button variant="outline" className="w-full border-blue-600 text-blue-600 hover:bg-blue-50"
                onClick={() => setIsModalOpen(true)}>
                    Register Your Business
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Expert Tips */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Expert tips for LLC owners
            </h2>
            <p className="text-lg text-gray-600">Follow these best practices to protect and grow your business</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: DollarSign,
                title: 'Maintain Separate Finances',
                description: 'Always keep your personal and business finances separate. Open a dedicated business bank account and use it exclusively for business transactions.',
              },
              {
                icon: FileText,
                title: 'Keep Good Records',
                description: 'Document all major business decisions, maintain meeting minutes, and keep thorough financial records to maintain your liability protection.',
              },
              {
                icon: Calendar,
                title: 'File Annual Reports',
                description: 'Most states require businesses to file annual reports and pay annual fees. Missing these deadlines can result in penalties or administrative dissolution.',
              },
              {
                icon: CreditCard,
                title: 'Get an EIN',
                description: 'Even if not required, getting an Employer Identification Number (EIN) helps establish business credit and is often needed for banking.',
              },
              {
                icon: FileCheck,
                title: 'Create an Operating Agreement',
                description: 'Even in states where it\'s not required, an operating agreement helps prevent disputes and clarifies ownership and management structure.',
              },
              {
                icon: ShieldCheck,
                title: 'Consider Business Insurance',
                description: 'While proper business registration protects personal assets, business insurance provides additional coverage for property, liability, and other risks.',
              },
            ].map((tip, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <tip.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{tip.title}</h3>
                      <p className="text-base text-gray-600">{tip.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setIsModalOpen(true)}
            >
              Get Legal Help
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
              onClick={openChat}
            >
              Chat Now
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Legal Flick */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why choose Legal Flick for your LLC?
            </h2>
            <p className="text-xl text-gray-600">
              Industry-leading service backed by expertise and technology
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                number: '500K+',
                label: 'LLCs Formed',
                description: 'Trusted by over half a million businesses',
              },
              {
                number: '99%',
                label: 'Approval Rate',
                description: 'Industry-leading filing accuracy',
              },
              {
                number: '24/7',
                label: 'Support Available',
                description: 'Get help whenever you need it',
              },
              {
                number: '$0',
                label: 'Starting Cost',
                description: 'Affordable packages for every budget',
              },
            ].map((stat, idx) => (
              <Card key={idx} className="text-center hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
                  <div className="font-semibold text-gray-900 mb-2">{stat.label}</div>
                  <div className="text-sm text-gray-600">{stat.description}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-8">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">100% Accuracy Guarantee</h3>
                <p className="text-gray-600 text-sm">
                  If we make an error, we will correct it at no additional cost to you
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Fast Processing</h3>
                <p className="text-gray-600 text-sm">
                  Most filings completed within 5-10 business days, with expedited options available
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Dedicated Support Team</h3>
                <p className="text-gray-600 text-sm">
                  Expert guidance from business specialists throughout the entire process
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Common Questions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Common LLC questions answered
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                question: 'Can I be my own registered agent?',
                answer: 'Yes, but we recommend using a professional registered agent service. They ensure you never miss important legal documents and provide privacy by keeping your home address off public records.',
              },
              {
                question: 'How many members do I need?',
                answer: 'You can form a single-member LLC with just yourself, or a multi-member LLC with multiple owners. There is no maximum number of members.',
              },
              {
                question: 'Do I need a business bank account?',
                answer: 'Yes, maintaining separate business finances is crucial for liability protection. Open a business bank account immediately after formation.',
              },
              {
                question: 'Can I change my LLC name later?',
                answer: 'Yes, you can file an amendment with your state to change your LLC name. This typically requires a filing fee and updated Articles of Organization.',
              },
              {
                question: 'What licenses do I need?',
                answer: 'Required licenses vary by industry and location. Common needs include general business licenses, professional licenses, and industry-specific permits.',
              },
              {
                question: 'Can foreign nationals form an LLC?',
                answer: 'Yes, non-US citizens can form and own an LLC. There are no citizenship or residency requirements for LLC ownership.',
              },
            ].map((item, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{item.question}</h3>
                  <p className="text-gray-600">{item.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setIsModalOpen(true)}
            >
              Get Legal Help
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
              onClick={openChat}
            >
              Chat Now
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700"></div>
        
        {/* Overlay Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block px-6 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white font-semibold mb-6">
              🎉 Limited Time Offer
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-white">
              Ready to Protect Your Business?
            </h2>
            
            <p className="text-xl lg:text-2xl mb-4 text-blue-100 max-w-3xl mx-auto">
              Join over 500,000 businesses that trust Legal Flick for their business registration
            </p>
            
            <p className="text-lg mb-10 text-blue-50 max-w-2xl mx-auto">
              Start your LLC today with our expert guidance and comprehensive support. Get your business legally protected in just minutes.
            </p>

            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6 mb-10">
              <Button
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-10 py-6 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                onClick={() => setIsModalOpen(true)}
              >
                Register Your Business
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 text-lg px-10 py-6"
                onClick={openChat}
              >
                  Chat Now
              </Button>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-3xl mx-auto text-center">
              <div className="backdrop-blur-sm bg-white/10 rounded-lg p-4">
                <div className="flex items-center justify-center mb-2">
                  <CheckCircle className="w-6 h-6 text-green-300 mr-2" />
                  <span className="text-white font-semibold">100% Accuracy</span>
                </div>
                <p className="text-blue-100 text-sm">Guaranteed filing accuracy</p>
              </div>
              
              <div className="backdrop-blur-sm bg-white/10 rounded-lg p-4">
                <div className="flex items-center justify-center mb-2">
                  <CheckCircle className="w-6 h-6 text-green-300 mr-2" />
                  <span className="text-white font-semibold">Fast Processing</span>
                </div>
                <p className="text-blue-100 text-sm">Most orders completed in 5-10 days</p>
              </div>
              
              <div className="backdrop-blur-sm bg-white/10 rounded-lg p-4">
                <div className="flex items-center justify-center mb-2">
                  <CheckCircle className="w-6 h-6 text-green-300 mr-2" />
                  <span className="text-white font-semibold">Expert Support</span>
                </div>
                <p className="text-blue-100 text-sm">24/7 customer assistance</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default RegisterBusinessPage;